import { ExtendedTrajectoryData } from '../services/trajectoryCalculationService';
import { MergedTrajectory } from '../types/sharedTypes';

/**
 * Merges min and max footprint curves into mainTrajectory object (for Recharts rendering).
 * @param trajectoryData
 * @returns
 */
export const mergeFootprintsIntoMainCurve = (
  trajectoryData: ExtendedTrajectoryData | null
) => {
  const mergedTrajectoryData: MergedTrajectory[] = [];

  if (!trajectoryData) {
    return mergedTrajectoryData;
  }

  const { mainTrajectory, maxFootprintTrajectory, minFootprintTrajectory } =
    trajectoryData.trajectoryGroup;

  // Trajectories may have different lengths (for example if cut-off at obstacle occured)
  const maxTrajectoryLength = Math.max(
    mainTrajectory.length,
    minFootprintTrajectory.length,
    maxFootprintTrajectory.length
  );

  for (let i = 0; i < maxTrajectoryLength; i++) {
    const minPoint = minFootprintTrajectory[i] ?? minFootprintTrajectory.at(-1);
    const maxPoint = maxFootprintTrajectory[i] ?? maxFootprintTrajectory.at(-1);
    const mainPoint = mainTrajectory[i] ?? mainTrajectory.at(-1);

    mergedTrajectoryData.push({
      x: mainPoint.x,
      y: mainPoint.y,
      maxFootprintX: maxPoint.x,
      maxFootprintY: maxPoint.y,
      minFootprintX: minPoint.x,
      minFootprintY: minPoint.y,
    });
  }

  return mergedTrajectoryData;
};

/**
 * Generate ticks needed for axis of chart.
 * @param trajectoryData
 * @param axis
 * @returns
 */
export const generateTicksForChart = (
  trajectoryData: ExtendedTrajectoryData | null
) => {
  if (trajectoryData) {
    // Split ticks, if window width is small (mobile) and more than x-meters.
    const split = () => {
      if (trajectoryData.range > 180) {
        if (window.innerWidth < 400) {
          return 40;
        }
        if (window.innerWidth < 700) {
          return 30;
        }
        if (window.innerWidth < 1200) {
          return 20;
        }
      }
      if (trajectoryData.range > 50) {
        if (window.innerWidth < 400) {
          return 30;
        }
        if (window.innerWidth < 700) {
          return 20;
        }
      }
      return 10;
    };

    // Round up to nearest 10 (or other, if split is higher).
    // get largest value (X or Y). Other axis gets hard-set from that axis.
    const roundedUp = Math.max(
      Math.ceil(trajectoryData.range / split()) * split(),
      Math.ceil(trajectoryData.highestPoint.y / split()) * split()
    );

    // Generate ticks.
    // If chart is 100m or larger, 10m-ticks.
    // Otherwise, generate 5m-ticks.
    // Split ticks, if window width is small (mobile) and more than 50m.
    const ticks: number[] = [];
    for (let i = 0; i <= roundedUp; i += split()) {
      ticks.push(i);
    }

    return ticks;
  }

  // Default if no data applied.
  return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
};
